import type { AlertFactory } from '@/features/shell'
import { type Coupon } from '@/features/billing/coupons/Coupon'
import { Member } from '@/features/iam/members/Member'
import { Job } from '@/features/jobs/Job'
import { action, computed, observable, runInAction } from 'mobx'
import { task } from 'mobx-task'
import { EMPTY, type Observable, firstValueFrom } from 'rxjs'
import { catchError, filter, first, switchMap, takeUntil } from 'rxjs/operators'
import { Id } from '@/infra/id'
import { fromStream } from '@/utils/mobx-util'
import { filterNonNil, fromAbortSignal, withAbortSignal } from '@/utils/rx-util'
import type { CouponModalViewModel } from '@/features/jobs/drafting/components/JobCreation/Submission/CouponModal/CouponModalViewModel'
import { type ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { type QueueChange } from './DraftPersister'
import type {
  AddCouponFn,
  FetchCouponFn,
  FetchMemberCouponsFn,
} from '@/features/billing'
import { type UserCoupon } from '@/features/billing/coupons/UserCoupon'

/**
 * Picks the coupon applied to the draft.
 */
export class CouponSelectionViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * The selected coupon, if any.
   */
  @observable accessor selected: Coupon | null = null

  /**
   * The text for the code that the user wants to add.
   */
  @observable accessor codeToAdd = ''

  /**
   * Streams as MobX Observables.
   */
  private readonly _$

  /**
   * The coupons of the selected member.
   */
  @observable private accessor _memberCoupons: UserCoupon[] = []

  constructor(
    private readonly _member$: Observable<Member | null>,
    private readonly _job$: Observable<Job>,
    private readonly _queueChange: QueueChange,
    private readonly _addCoupon: AddCouponFn,
    private readonly _fetchMemberCoupons: FetchMemberCouponsFn,
    private readonly _fetchCoupon: FetchCouponFn,
    private readonly _isDisabled: () => boolean,
    private readonly _couponModalVm: CouponModalViewModel,
    private readonly _newAlert: AlertFactory,
  ) {
    this._$ = {
      member: fromStream(null, this._member$),
      job: fromStream(Job.none, this._job$),
    }

    // Fetch coupons whenever the Member changes, once the modal shows.
    this._lifecycle.manage(
      this._member$.pipe(
        filterNonNil(),
        switchMap((member) =>
          this._couponModalVm.modal.showing$.pipe(
            filter(
              (showing) => showing && this._couponModalVm.selectionVm === this,
            ),
            first(),
            switchMap(() =>
              withAbortSignal((signal) =>
                this.fetchCoupons(member, signal),
              ).pipe(
                // The coupon modal shows the failure.
                catchError(() => EMPTY),
              ),
            ),
          ),
        ),
      ),
    )
  }

  /**
   * Whether a coupon action is currently pending.
   */
  @computed
  get isPending() {
    return (
      this.fetchCouponInfo.pending || this.select.pending || this.remove.pending
    )
  }

  /**
   * Whether coupon selection controls are disabled.
   */
  @computed
  get disabled() {
    return this._isDisabled()
  }

  /**
   * Whether we can use coupons.
   */
  @computed
  get canUseCoupons() {
    return this._$.job().workspace.config.billing.couponsEnabled
  }

  /**
   * The available coupons.
   */
  @computed
  get coupons() {
    return this._$.member()
      ? this._memberCoupons.filter((uc) => !uc.redeemed).map((c) => c.coupon)
      : []
  }

  /**
   * Whether a coupon is selected.
   */
  @computed
  get isCouponSelected() {
    return Boolean(this.selected)
  }

  /**
   * Whether we can add a coupon.
   */
  @computed
  get canAdd() {
    return (
      this.codeToAdd.length > 0 &&
      !this.addCoupon.pending &&
      !this.select.pending
    )
  }

  /**
   * Whether the given coupon is the selected one.
   *
   * @param coupon
   */
  isSelected(coupon: Coupon) {
    return this.selected === coupon
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
    this._closeDialog()
  }

  /**
   * Shows the coupon picker dialog.
   */
  @action.bound
  showDialog() {
    this._couponModalVm.show(this)
  }

  /**
   * Fetches the applied coupon.
   *
   * @param appliedCouponId
   * @param signal
   */
  fetchCouponInfo = task.resolved(
    async (appliedCouponId: string | null, signal: AbortSignal) => {
      // Wait until the selected Member has loaded.
      await this._waitForMember(signal)

      const couponId = Id(appliedCouponId)
      if (this.selected?.id === couponId) {
        return
      }

      if (!couponId) {
        this.setCoupon(null)
        return
      }

      const coupon = await this._fetchCoupon(couponId, signal)
      signal.throwIfAborted()
      this.setCoupon(coupon)
    },
  )

  /**
   * Fetches coupons for the specified Workspace Member.
   */
  fetchCoupons = task(async (member: Member, signal?: AbortSignal) => {
    const memberCoupons = await this._fetchMemberCoupons(member, signal)
    runInAction(() => {
      this._memberCoupons = memberCoupons
    })
  })

  /**
   * Selects the coupon.
   *
   * @param coupon
   */
  select = task.resolved(async (coupon: Coupon | null) => {
    // A failure is alerted by the draft editor.
    const result = await this._queueChange(
      coupon ? { coupon, type: 'CouponSelected' } : { type: 'CouponCleared' },
    )

    this._closeDialog()
    return !result.errorMessage
  })

  /**
   * Attempts to add a coupon.
   */
  addCoupon = task.resolved(async () => {
    const code = this.codeToAdd.trim()
    if (!code) {
      throw new Error('Please enter a code.')
    }

    const member = this._$.member()
    if (!member) {
      throw new Error('No workspace selected.')
    }

    const memberCoupon = await this._addCoupon(member, code)
    if (!memberCoupon) {
      throw new Error('Oops! That does not appear to be a valid coupon!')
    }

    runInAction(() => {
      if (!this._memberCoupons.includes(memberCoupon)) {
        this._memberCoupons = [...this._memberCoupons, memberCoupon]
      }
    })

    if (!(await this.select(memberCoupon.coupon))) {
      return
    }

    this._newAlert()
      .saying('Success!', 'Your discount has been applied.')
      .withState('success')
      .autoDismiss()
      .show()
  })

  /**
   * Sets the code.
   *
   * @param code
   */
  @action.bound
  setCode(code: string) {
    // Whenever we type something, reset the task state
    // so any displayed error disappears.
    this.addCoupon.reset()
    this.codeToAdd = code.toUpperCase()
  }

  /**
   * Sets the coupon.
   *
   * @param coupon
   */
  @action.bound
  setCoupon(coupon: Coupon | null) {
    this.selected = coupon
  }

  /**
   * Clears the coupon.
   */
  remove = task.resolved(async () => {
    return this.select(null)
  })

  private _closeDialog() {
    if (this._couponModalVm.selectionVm === this) {
      this._couponModalVm.modal.close()
    }
  }

  /**
   * Waits for the Workspace Member to have been selected.
   * @private
   */
  private async _waitForMember(signal: AbortSignal) {
    await firstValueFrom(
      this._member$.pipe(
        first((m) => m !== Member.none),
        takeUntil(fromAbortSignal(signal)),
      ),
      { defaultValue: null },
    )
    signal.throwIfAborted()
  }
}

/**
 * Factory for creating a {@link CouponSelectionViewModel}.
 */
export type CouponSelectionViewModelFactory = ReturnType<
  typeof createCouponSelectionViewModelFactory
>

/** @see CouponSelectionViewModelFactory */
export function createCouponSelectionViewModelFactory(
  addCoupon: AddCouponFn,
  fetchMemberCoupons: FetchMemberCouponsFn,
  fetchCoupon: FetchCouponFn,
  couponModalVm: CouponModalViewModel,
  newAlert: AlertFactory,
) {
  return (
    member$: Observable<Member | null>,
    job$: Observable<Job>,
    queueChange: QueueChange,
    isDisabled: () => boolean,
  ) =>
    new CouponSelectionViewModel(
      member$,
      job$,
      queueChange,
      addCoupon,
      fetchMemberCoupons,
      fetchCoupon,
      isDisabled,
      couponModalVm,
      newAlert,
    )
}
